import type { SVGProps } from "react";

/**
 * Stack marks — Concept C. Three offset parallelograms stacked vertically.
 * Reads as layered systems / frontend-backend-infra.
 *
 * Treatments: Primary (tri-gradient top→bottom cyan→violet), MonoLight,
 * MonoDark, Accent.
 * Source of truth: tasks/BRAND_IDENTITY.md §4.
 */

type MarkProps = Omit<SVGProps<SVGSVGElement>, "viewBox">;

const topPts = "16,14 52,14 48,24 12,24";
const midPts = "14,28 50,28 46,38 10,38";
const botPts = "12,42 48,42 44,52 8,52";

export function StackPrimary({ titleId = "stack-primary-title", ...props }: MarkProps & { titleId?: string }) {
  const topId = `${titleId}-top`;
  const midId = `${titleId}-mid`;
  const botId = `${titleId}-bot`;
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-labelledby={titleId} {...props}>
      <title id={titleId}>NEXIRT</title>
      <defs>
        <linearGradient id={topId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#22D3EE" />
          <stop offset="1" stopColor="#0EA5C7" />
        </linearGradient>
        <linearGradient id={midId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#6E7FFF" />
          <stop offset="1" stopColor="#4B5CFF" />
        </linearGradient>
        <linearGradient id={botId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#6B4FE6" />
          <stop offset="1" stopColor="#5B3EDB" />
        </linearGradient>
      </defs>
      <polygon points={topPts} fill={`url(#${topId})`} />
      <polygon points={midPts} fill={`url(#${midId})`} />
      <polygon points={botPts} fill={`url(#${botId})`} />
    </svg>
  );
}

export function StackMonoLight(props: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NEXIRT" {...props}>
      <g fill="#F5F5F5">
        <polygon points={topPts} />
        <polygon points={midPts} />
        <polygon points={botPts} />
      </g>
    </svg>
  );
}

export function StackMonoDark(props: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NEXIRT" {...props}>
      <g fill="#0A0B10">
        <polygon points={topPts} />
        <polygon points={midPts} />
        <polygon points={botPts} />
      </g>
    </svg>
  );
}

export function StackAccent(props: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NEXIRT" {...props}>
      <g fill="#6B4FE6">
        <polygon points={topPts} />
        <polygon points={midPts} />
        <polygon points={botPts} />
      </g>
    </svg>
  );
}
